import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  trackPageView,
  trackClick,
  trackNavigation,
  trackLanguageSwitch,
} from '../utils/analytics';
import { hasPassedOnboarding } from '../utils/userSelections';

// Import onboarding images from assets
import onboarding1 from '../assets/onboarding1.webp';
import onboarding2 from '../assets/onboarding2.webp';
import onboarding3 from '../assets/onboarding3.webp';
import onboarding4 from '../assets/onboarding4.webp';
import onboarding5 from '../assets/onboarding5.webp';

// Color constants
const BLUE_ACCENT = '#3B7BF4';
const GOLD_BUTTON = '#F5C842';
const TEXT_DARK = '#1A1A2E';

interface OnboardingStep {
  titleKey: string;
  titleHighlightKey: string;
  subtitleKey: string;
  image: string;
  isLastStep?: boolean;
}

const AppOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [, setIsTransitioning] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [currentLang, setCurrentLang] = useState<'en' | 'he'>(
    (i18n.language || 'he').startsWith('he') ? 'he' : 'en',
  );
  const isRtl = currentLang === 'he'; // RTL for Hebrew, LTR for English

  // Touch/swipe handling
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // All onboarding images for preloading
  const allImages = [onboarding1, onboarding2, onboarding3, onboarding4, onboarding5];

  // Preload all images on mount
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = allImages.map((src) => {
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to not block
          img.src = src;
        });
      });
      await Promise.all(imagePromises);
      setImagesLoaded(true);
    };
    preloadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize language from localStorage/i18n
  useEffect(() => {
    let savedLang = i18n.language || localStorage.getItem('i18nextLng');

    // If savedLang is a browser language code (like "en-US"), normalize to "en" or "he"
    if (savedLang && savedLang.includes('-')) {
      if (savedLang.toLowerCase().startsWith('he') || savedLang.toLowerCase().startsWith('iw')) {
        savedLang = 'he';
      } else {
        // Normalize other variants (e.g. en-US) to "en" so user's English choice is preserved
        savedLang = 'en';
      }
      localStorage.setItem('i18nextLng', savedLang);
    }

    // Default to Hebrew if no language is set
    const normalizedLang =
      savedLang && (savedLang.startsWith('he') || savedLang === 'en')
        ? savedLang.startsWith('he')
          ? 'he'
          : 'en'
        : 'he';

    if (normalizedLang !== currentLang) {
      setCurrentLang(normalizedLang);
      i18n.changeLanguage(normalizedLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if user has already passed the onboarding (login page)
  useEffect(() => {
    // If a connection deep-link is present, skip the carousel entirely
    const urlParams = new URLSearchParams(window.location.search);
    const connectionId = urlParams.get('connectionId');
    if (connectionId) {
      navigate(`/onboarding?connectionId=${encodeURIComponent(connectionId)}`, { replace: true });
      return;
    }

    const hasSeenOnboarding = localStorage.getItem('hasSeenAppOnboarding');
    const hasLoggedInUser = !!localStorage.getItem('loggedInUser');

    // If user has passed the onboarding/login page AND has a logged-in user, go directly to user portal
    if (hasPassedOnboarding() && hasLoggedInUser) {
      trackNavigation('AppOnboarding', 'UserPortal', 'auto_redirect');
      navigate('/user-portal', { replace: true });
      return;
    }

    // If user has seen app onboarding (carousel), go to login/OnboardingPage
    if (hasSeenOnboarding === 'true') {
      navigate('/onboarding', { replace: true });
    }
    trackPageView('AppOnboarding');
  }, [navigate]);

  const steps: OnboardingStep[] = [
    {
      titleKey: 'app_onboarding_step1_title',
      titleHighlightKey: 'app_onboarding_step1_highlight',
      subtitleKey: 'app_onboarding_step1_subtitle',
      image: onboarding3, // Bar mitzvah scene
    },
    {
      titleKey: 'app_onboarding_step2_title',
      titleHighlightKey: 'app_onboarding_step2_highlight',
      subtitleKey: 'app_onboarding_step2_subtitle',
      image: onboarding2, // Boy with phone at Torah - AI learning
    },
    {
      titleKey: 'app_onboarding_step3_title',
      titleHighlightKey: 'app_onboarding_step3_highlight',
      subtitleKey: 'app_onboarding_step3_subtitle',
      image: onboarding1, // Boy with Rabbi
    },
    {
      titleKey: 'app_onboarding_step4_title',
      titleHighlightKey: 'app_onboarding_step4_highlight',
      subtitleKey: 'app_onboarding_step4_subtitle',
      image: onboarding4, // Boy on beach
    },
    {
      titleKey: 'app_onboarding_step5_title',
      titleHighlightKey: 'app_onboarding_step5_highlight',
      subtitleKey: 'app_onboarding_step5_subtitle',
      image: onboarding5, // Boy with headphones
      isLastStep: true,
    },
  ];

  // Swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX; // Reset to same value to avoid stale data
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;
    // diff > 0 = finger moved left; diff < 0 = finger moved right

    // English (LTR): swipe right → next, swipe left → previous
    // Hebrew (RTL): swipe left → next, swipe right → previous
    if (!isRtl) {
      if (diff > swipeThreshold && currentStep < steps.length - 1) {
        changeStep(currentStep + 1); // swipe left → next
      } else if (diff < -swipeThreshold && currentStep > 0) {
        changeStep(currentStep - 1); // swipe right → previous
      }
    } else {
      if (diff < -swipeThreshold && currentStep < steps.length - 1) {
        changeStep(currentStep + 1); // swipe right → next
      } else if (diff > swipeThreshold && currentStep > 0) {
        changeStep(currentStep - 1); // swipe left → previous
      }
    }
  }, [currentStep, isRtl, steps.length]);

  // Helper to change step with transition
  const changeStep = (newStep: number) => {
    setIsTransitioning(true);
    setCurrentStep(newStep);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleNext = () => {
    trackClick(`AppOnboarding Step ${currentStep + 1} Next`, 'AppOnboarding');
    if (currentStep < steps.length - 1) {
      changeStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleSkip = () => {
    trackClick('AppOnboarding Skip', 'AppOnboarding');
    completeOnboarding();
  };

  const completeOnboarding = () => {
    localStorage.setItem('hasSeenAppOnboarding', 'true');
    trackNavigation('AppOnboarding', 'Onboarding', 'complete');
    navigate('/onboarding', { replace: true });
  };

  const handleDotClick = (index: number) => {
    if (index === currentStep) return;
    trackClick(`AppOnboarding Dot ${index + 1}`, 'AppOnboarding');
    changeStep(index);
  };

  const handleBack = () => {
    trackClick(`AppOnboarding Step ${currentStep + 1} Back`, 'AppOnboarding');
    if (currentStep > 0) {
      changeStep(currentStep - 1);
    }
  };

  // Handle language switch
  const handleLanguageSwitch = () => {
    const newLang = currentLang === 'he' ? 'en' : 'he';
    trackLanguageSwitch(currentLang, newLang);
    setCurrentLang(newLang);
    i18n.changeLanguage(newLang);
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStepData.isLastStep;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        flex: 1,
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "var(--kol-font-sans, 'Heebo', sans-serif)",
        overflow: 'hidden',
        position: 'relative',
        background: '#FFFFFF',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      {/* Language switcher button - top left, only shown on first step */}
      {currentStep === 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleLanguageSwitch();
          }}
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            background: '#F0F0F0',
            border: '1px solid #E0E0E0',
            borderRadius: '6px',
            color: '#1A1A2E',
            fontSize: '10px',
            fontWeight: 500,
            cursor: 'pointer',
            zIndex: 11,
            padding: '6px 11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s ease, transform 0.1s ease, opacity 0.2s ease',
            textTransform: 'uppercase',
            fontFamily: "var(--kol-font-sans, 'Heebo', sans-serif)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#E5E5E5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#F0F0F0';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.98)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          aria-label={currentLang === 'he' ? 'Switch to English' : 'עבור לעברית'}
        >
          {currentLang === 'he' ? 'ENG' : 'עברית'}
        </button>
      )}

      {/* Back button - always rendered to maintain layout, invisible on first step */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (currentStep > 0) {
            handleBack();
          }
        }}
        style={{
          position: 'absolute',
          top: 16,
          [isRtl ? 'right' : 'left']: 16,
          background: 'transparent',
          border: 'none',
          color: '#6B7280',
          fontSize: '14px',
          fontWeight: 500,
          cursor: currentStep > 0 ? 'pointer' : 'default',
          zIndex: 10,
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          opacity: currentStep > 0 ? 1 : 0,
          pointerEvents: currentStep > 0 ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
          direction: isRtl ? 'rtl' : 'ltr',
        }}
        aria-hidden={currentStep === 0}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          style={{
            transform: isRtl ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <path
            d="M12 4L6 10L12 16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t('app_onboarding_back')}
      </button>

      {/* Header with title - fixed height to prevent layout shifts */}
      <div
        style={{
          padding: '48px 24px 0',
          textAlign: 'center',
          direction: isRtl ? 'rtl' : 'ltr',
          minHeight: 'calc(48px + 2.6em + 16px)', // 48px top padding + 2 lines of text (1.3 * 2) + bottom spacing
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <h1
          style={{
            fontSize: 'clamp(24px, 7vw, 32px)',
            fontWeight: 700,
            color: TEXT_DARK,
            margin: 0,
            lineHeight: 1,
            maxWidth: '100%',
            height: '70px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <span>{t(currentStepData.titleKey)}</span>
          <span style={{ color: BLUE_ACCENT }}>{t(currentStepData.titleHighlightKey)}</span>
        </h1>
      </div>

      {/* Image area - Fixed position container with all images preloaded */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          minHeight: '50vh',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Render all images stacked, only show current one */}
          {allImages.map((imageSrc, index) => (
            <img
              key={index}
              src={imageSrc}
              alt={String(t(steps[index].titleKey))}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                maskImage:
                  'linear-gradient(to bottom, transparent 0%, black 8%, black 85%, transparent 100%)',
                WebkitMaskImage:
                  'linear-gradient(to bottom, transparent 0%, black 8%, black 85%, transparent 100%)',
                opacity: index === currentStep ? 1 : 0,
                transition: 'opacity 0.3s ease-out',
                pointerEvents: index === currentStep ? 'auto' : 'none',
              }}
              draggable={false}
            />
          ))}

          {/* Loading indicator while images preload */}
          {!imagesLoaded && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.8)',
                zIndex: 5,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: `3px solid ${BLUE_ACCENT}`,
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0.5; transform: scale(0.98); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Bottom section */}
      <div
        style={{
          padding: '0 24px',
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
          textAlign: 'center',
        }}
      >
        {/* Step dots - RTL: progress right→left (step 0 on right); LTR: progress left→right (step 0 on left) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            direction: isRtl ? 'rtl' : 'ltr',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 20,
          }}
        >
          {steps.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDotClick(index);
              }}
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: index === currentStep ? TEXT_DARK : '#D1D5DB',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                padding: 0,
              }}
              aria-label={`Step ${index + 1}`}
            />
          ))}
        </div>

        {/* Subtitle */}
        <p
          style={{
            fontSize: 'clamp(16px, 4.5vw, 20px)',
            color: TEXT_DARK,
            marginBottom: 24,
            lineHeight: 1.5,
            direction: isRtl ? 'rtl' : 'ltr',
            fontWeight: 500,
            height: '50px',
          }}
        >
          {t(currentStepData.subtitleKey)}
        </p>

        {/* Action button - Gold for last step, Blue for others */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          style={{
            width: '100%',
            maxWidth: '320px',
            padding: '16px 24px',
            borderRadius: 28,
            border: 'none',
            background: isLastStep ? GOLD_BUTTON : BLUE_ACCENT,
            color: isLastStep ? TEXT_DARK : '#FFFFFF',
            fontSize: '18px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            margin: '0 auto',
            boxShadow: isLastStep
              ? '0 4px 16px rgba(245, 200, 66, 0.4)'
              : '0 4px 16px rgba(59, 123, 244, 0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            direction: isRtl ? 'rtl' : 'ltr',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {isLastStep ? t('app_onboarding_get_started') : t('app_onboarding_next')}
        </button>

        {/* Skip button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSkip();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: TEXT_DARK,
            fontSize: '16px',
            fontWeight: 500,
            cursor: 'pointer',

            textDecoration: 'underline',
          }}
        >
          {t('app_onboarding_skip')}
        </button>
      </div>
    </div>
  );
};

export default AppOnboardingPage;
